import type { ReactNode } from 'react'
import type { Patient } from '@/lib/clinic/types'
import type { SearchPurpose } from '@/components/app/patient-search-dialog'
import type { ContactChannel } from '@/components/app/reminder-dialog'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import { useRouter } from 'next/router'
import { PatientSearchDialog } from '@/components/app/patient-search-dialog'
import { FollowUpDialog } from '@/components/app/follow-up-dialog'
import { ReminderDialog } from '@/components/app/reminder-dialog'
import { PatientQuickView } from '@/components/app/patient-quick-view'

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from '@/components/ui/alert-dialog'

export interface ConfirmOptions {
    title: string
    description?: ReactNode
    confirmLabel?: string
    cancelLabel?: string
    destructive?: boolean
    onConfirm: () => void
}

export interface FollowUpRequest {
    patientId?: string | null
    followUpId?: string | null
    visitId?: string | null
}

export interface ReminderRequest {
    patientId: string
    followUpId?: string | null
    channel?: ContactChannel
}

interface AppActionsValue {
    openSearch: (purpose?: SearchPurpose) => void
    openFollowUp: (request?: FollowUpRequest) => void
    openReminder: (request: ReminderRequest) => void
    openQuickView: (patientId: string) => void
    confirm: (options: ConfirmOptions) => void
}

const AppActionsContext = createContext<AppActionsValue | null>(null)

interface PanelState<T> {
    open: boolean
    key: number
    request: T
}

export const AppActionsProvider = ({ children }: { children: ReactNode }): ReactNode => {
    const router = useRouter()
    const [search, setSearch] = useState<PanelState<SearchPurpose>>({ open: false, key: 0, request: 'profile' })
    const [followUp, setFollowUp] = useState<PanelState<FollowUpRequest>>({ open: false, key: 0, request: {} })
    const [reminder, setReminder] = useState<PanelState<ReminderRequest | null>>({ open: false, key: 0, request: null })
    const [quickView, setQuickView] = useState<PanelState<string | null>>({ open: false, key: 0, request: null })
    const [confirmState, setConfirmState] = useState<{ open: boolean, options: ConfirmOptions | null }>({ open: false, options: null })

    const closeQuickView = useCallback((): void => {
        setQuickView(previous => ({ ...previous, open: false }))
    }, [])

    const openSearch = useCallback((purpose: SearchPurpose = 'profile'): void => {
        setSearch(previous => ({ open: true, key: previous.key + 1, request: purpose }))
    }, [])

    const openFollowUp = useCallback((request: FollowUpRequest = {}): void => {
        closeQuickView()
        setFollowUp(previous => ({ open: true, key: previous.key + 1, request }))
    }, [closeQuickView])

    const openReminder = useCallback((request: ReminderRequest): void => {
        closeQuickView()
        setReminder(previous => ({ open: true, key: previous.key + 1, request }))
    }, [closeQuickView])

    const openQuickView = useCallback((patientId: string): void => {
        setQuickView(previous => ({ open: true, key: previous.key + 1, request: patientId }))
    }, [])

    const confirm = useCallback((options: ConfirmOptions): void => {
        setConfirmState({ open: true, options })
    }, [])

    const handleSearchSelect = useCallback((patient: Patient, purpose: SearchPurpose): void => {
        if (purpose === 'follow-up') {
            openFollowUp({ patientId: patient.id })

            return
        }

        const href = purpose === 'visit'
            ? `/patients/${patient.id}/visits/new`
            : purpose === 'prescription'
                ? `/prescriptions/new?patient=${patient.id}`
                : `/patients/${patient.id}`

        void router.push(href)
    }, [router, openFollowUp])

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault()
                openSearch('profile')
            }
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [openSearch])

    useEffect(() => {
        router.events.on('routeChangeStart', closeQuickView)

        return () => router.events.off('routeChangeStart', closeQuickView)
    }, [router.events, closeQuickView])

    const value = useMemo<AppActionsValue>(() => ({
        openSearch,
        openFollowUp,
        openReminder,
        openQuickView,
        confirm
    }), [openSearch, openFollowUp, openReminder, openQuickView, confirm])

    const options = confirmState.options

    return (
        <AppActionsContext.Provider value={value}>
            {children}

            {search.key > 0 && (
                <PatientSearchDialog
                    key={search.key}
                    open={search.open}
                    purpose={search.request}
                    onOpenChange={open => setSearch(previous => ({ ...previous, open }))}
                    onSelect={handleSearchSelect}
                />
            )}

            {followUp.key > 0 && (
                <FollowUpDialog
                    key={followUp.key}
                    open={followUp.open}
                    patientId={followUp.request.patientId ?? null}
                    followUpId={followUp.request.followUpId ?? null}
                    visitId={followUp.request.visitId ?? null}
                    onOpenChange={open => setFollowUp(previous => ({ ...previous, open }))}
                    onRequestReminder={(patientId, followUpId) => openReminder({ patientId, followUpId, channel: 'whatsapp' })}
                />
            )}

            {reminder.key > 0 && reminder.request && (
                <ReminderDialog
                    key={reminder.key}
                    open={reminder.open}
                    patientId={reminder.request.patientId}
                    followUpId={reminder.request.followUpId ?? null}
                    initialChannel={reminder.request.channel ?? 'whatsapp'}
                    onOpenChange={open => setReminder(previous => ({ ...previous, open }))}
                    onReschedule={followUpId => openFollowUp({ followUpId })}
                />
            )}

            {quickView.key > 0 && (
                <PatientQuickView
                    key={quickView.key}
                    open={quickView.open}
                    patientId={quickView.request}
                    onOpenChange={open => setQuickView(previous => ({ ...previous, open }))}
                    onContact={(patientId, channel) => openReminder({ patientId, channel })}
                    onAddFollowUp={patientId => openFollowUp({ patientId })}
                />
            )}

            <AlertDialog open={confirmState.open} onOpenChange={open => setConfirmState(previous => ({ ...previous, open }))}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {options?.title}
                        </AlertDialogTitle>

                        {options?.description && (
                            <AlertDialogDescription>
                                {options.description}
                            </AlertDialogDescription>
                        )}
                    </AlertDialogHeader>

                    <AlertDialogFooter>
                        <AlertDialogCancel>
                            {options?.cancelLabel ?? 'Cancel'}
                        </AlertDialogCancel>

                        <AlertDialogAction
                            className={options?.destructive ? 'bg-destructive text-white shadow-none hover:bg-destructive/90' : undefined}
                            onClick={() => {
                                options?.onConfirm()
                                setConfirmState(previous => ({ ...previous, open: false }))
                            }}
                        >
                            {options?.confirmLabel ?? 'Confirm'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AppActionsContext.Provider>
    )
}

export const useAppActions = (): AppActionsValue => {
    const context = useContext(AppActionsContext)

    if (!context) throw new Error('useAppActions must be used inside AppActionsProvider')

    return context
}
